/**
 * Swap a material — Board 4 (`docs/visit-page.md` §9): A fixed price, B time
 * & materials, C blocked.
 *
 * Swap is easy until it is bought, and the approved quote is never edited
 * (do-not-substitute 7). On a fixed-price (or per-visit) visit the customer's
 * price does not change and the visit pays the difference — shown in money
 * before Swap; on time & materials the invoice follows what is used at the
 * new price. The sheet may ask the salesperson for a change order — a task —
 * and never makes one.
 *
 * `GET …/materials/:line/swap-options` decides which sheet it is (`choices`
 * or `blocked`) and carries every figure; `POST …/swap` does it. A popup of
 * Slice 6's `sheet.tsx` (472 wide with its border; 402 blocked), and Board
 * 16's sheet from the bottom on a phone.
 */
import { useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import type {
  MaterialCatalog, MaterialLine, MaterialSource, SwapChoice, SwapOptions, SwapReason, VisitRead, VisitTabKey,
} from '@rowted/shared'
import { MATERIAL_SOURCES, SWAP_REASONS } from '@rowted/shared'
import { cn } from '../lib/cn.js'
import { request } from '../lib/api.js'
import { visitPath, useVisitWrite, type VisitWriteResult } from './api.js'
import { VisitButton, VisitPopup, VisitTile, VISIT_LAYER } from './sheet.js'
import { VisitGlyph } from './glyphs.js'
import { COMMON, MATERIALS, ORDER, RAIL, SWAP } from './copy.js'
import { money, signedMoney, timeIn } from './format.js'
import {
  MaterialChip, count, poHref, qtyText,
} from './materials-parts.js'
import { ChoiceChip, FIELD_BOX, FIELD_INPUT, FieldLabel, RadioDot, SLOT, TickBox, fill } from './fields.js'
import { InternalChip } from './internal-chip.js'

export function SwapSheet({ read, line, phone, tab = 'materials', onClose, onDone }: {
  read: VisitRead
  line: MaterialLine
  phone: boolean
  /** The tab whose fresh read the write asks for. */
  tab?: VisitTabKey
  onClose: () => void
  onDone: (result: VisitWriteResult) => void
}) {
  const [options, setOptions] = useState<SwapOptions | null>(null)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    let live = true
    request<SwapOptions>(visitPath(read.id, `/materials/${encodeURIComponent(line.id)}/swap-options`))
      .then((body) => { if (live) setOptions(body) })
      .catch((cause: unknown) => { if (live) setError(cause instanceof Error ? cause.message : String(cause)) })
    return () => { live = false }
  }, [read.id, line.id])

  if (!options) {
    return (
      <VisitPopup
        width={472}
        phone={phone}
        title={SWAP.title(line.names.shortName)}
        mark={<VisitTile glyph="swap" tone="amber" size={34} />}
        onClose={onClose}
      >
        {error && <p className="m-0 text-[12.5px] text-danger">{error}</p>}
      </VisitPopup>
    )
  }
  return options.kind === 'blocked'
    ? <Blocked read={read} line={line} options={options} phone={phone} onClose={onClose} />
    : <Choices read={read} line={line} options={options} phone={phone} tab={tab} onClose={onClose} onDone={onDone} />
}

// ---------------------------------------------------------------------------
// A and B — not bought yet
// ---------------------------------------------------------------------------

type Pick = 'best' | 'stock' | 'picked'

function Choices({ read, line, options, phone, tab, onClose, onDone }: {
  read: VisitRead
  line: MaterialLine
  options: Extract<SwapOptions, { kind: 'choices' }>
  phone: boolean
  tab: VisitTabKey
  onClose: () => void
  onDone: (result: VisitWriteResult) => void
}) {
  const write = useVisitWrite(read.id)
  const [reason, setReason] = useState<SwapReason | null>(null)
  const [picked, setPicked] = useState<SwapChoice | null>(options.picked)
  const [choice, setChoice] = useState<Pick | null>(options.bestMatch ? 'best' : options.inStock ? 'stock' : null)
  const [askChangeOrder, setAskChangeOrder] = useState(options.defaults.askChangeOrder)
  const [tellCustomer, setTellCustomer] = useState(options.defaults.tellCustomer)
  const [tellCrew, setTellCrew] = useState(options.defaults.tellCrew)
  const [finding, setFinding] = useState(false)
  const [split, setSplit] = useState<{ qty: string; source: MaterialSource } | null>(null)
  const [splitPriced, setSplitPriced] = useState<SwapChoice | null>(null)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const chosen = choice === 'best' ? options.bestMatch : choice === 'stock' ? options.inStock : choice === 'picked' ? picked : null
  const tm = options.billing === 'time_materials'
  const fixedLike = !tm
  const { line: swapLine } = options

  /* A split's part is priced for its own quantity — the server's figures, asked for again. */
  useEffect(() => {
    setSplitPriced(null)
    const qty = Number(split?.qty)
    if (!split || !chosen || !(qty > 0)) return
    let live = true
    request<SwapOptions>(`${visitPath(read.id, `/materials/${encodeURIComponent(line.id)}/swap-options`)}?itemId=${encodeURIComponent(chosen.item.id)}&qty=${encodeURIComponent(split.qty)}`)
      .then((body) => { if (live && body.kind === 'choices') setSplitPriced(body.picked) })
      .catch(() => { /* the paragraph keeps the whole line's figures */ })
    return () => { live = false }
  }, [split, chosen, read.id, line.id])

  const money2 = (splitPriced ?? chosen)?.internal?.money ?? null

  async function swap() {
    if (!reason || !chosen) return
    setBusy(true)
    setError(null)
    try {
      const result = await write('POST', `/materials/${encodeURIComponent(line.id)}/swap`, {
        version: swapLine.version,
        toItemId: chosen.item.id,
        reason,
        ...(split && Number(split.qty) > 0 ? { split: { qty: Number(split.qty), source: split.source } } : {}),
        askChangeOrder: fixedLike ? askChangeOrder : false,
        tellCustomer: tm ? tellCustomer : false,
        tellCrew,
      }, { tab })
      onDone(result)
      onClose()
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : String(cause))
    } finally {
      setBusy(false)
    }
  }

  const sub = tm ? SWAP.subTimeAndMaterials : SWAP.subFixed(read.reference)
  const day = swapLine.day !== null ? String(swapLine.day) : ''
  const lineSub = swapLine.internal
    ? SWAP.fromLine(qtyText(swapLine.qty, swapLine.unit), day, money(swapLine.internal.unitEstimate), swapLine.quoteReference ?? '')
    : swapLine.quoteReference
      ? COMMON.fromQuote(SWAP.qtyDay(qtyText(swapLine.qty, swapLine.unit), day), swapLine.quoteReference)
      : SWAP.qtyDay(qtyText(swapLine.qty, swapLine.unit), day)

  return (
    <VisitPopup
      width={472}
      phone={phone}
      title={SWAP.title(swapLine.names.shortName)}
      sub={sub}
      mark={<VisitTile glyph="swap" tone="amber" size={34} />}
      onClose={onClose}
      layer={VISIT_LAYER}
      asDrawn={tm ? 'vp-04 B' : 'vp-04 A'}
      footer={(
        <>
          <VisitButton size={phone ? 'phone' : 'popup'} onClick={onClose}>{SWAP.cancel}</VisitButton>
          <VisitButton
            size={phone ? 'phone' : 'popup'}
            tone="primary"
            glyph="swap"
            disabled={busy || !reason || !chosen}
            onClick={() => void swap()}
          >
            {SWAP.swap}
          </VisitButton>
        </>
      )}
    >
      <div className="flex flex-col gap-[13px]">
        <LineBox
          name={swapLine.item.name}
          sub={lineSub}
          chip={<MaterialChip state={swapLine.state} />}
        />

        <div className="min-w-0">
          <FieldLabel required>{SWAP.why}</FieldLabel>
          <div role="radiogroup" aria-label={SWAP.why} className="flex flex-wrap gap-1.5">
            {SWAP_REASONS.map((key) => (
              <ChoiceChip key={key} size={28} on={reason === key} onClick={() => setReason(key)}>
                {SWAP.reasons[key]}
              </ChoiceChip>
            ))}
          </div>
        </div>

        <div className="min-w-0">
          <FieldLabel required>{SWAP.swapTo}</FieldLabel>
          <div role="radiogroup" aria-label={SWAP.swapTo} className="flex flex-col gap-[7px]">
            {options.bestMatch && (
              <ChoiceCard choice={options.bestMatch} tag={SWAP.bestMatch} on={choice === 'best'} onPick={() => setChoice('best')} />
            )}
            {options.inStock && (
              <ChoiceCard choice={options.inStock} tag={SWAP.inStock} on={choice === 'stock'} onPick={() => setChoice('stock')} />
            )}
            {picked && (
              <ChoiceCard choice={picked} tag={null} on={choice === 'picked'} onPick={() => setChoice('picked')} />
            )}
            <button
              type="button"
              onClick={() => setFinding((was) => !was)}
              className="px-0.5 py-0.5 text-left text-[12px] font-semibold text-accent hover:text-accent-dark"
            >
              {SWAP.findAnother}
            </button>
            {finding && (
              <FindAnother
                read={read}
                line={line}
                split={split}
                onSplit={setSplit}
                onPicked={(found) => { setPicked(found); setChoice('picked') }}
              />
            )}
          </div>
        </div>

        {money2 && (
          <div className="rounded-[10px] border border-line bg-surface-internal px-3 py-[11px] text-[12.5px] leading-[1.5] text-text-body">
            <div className="flex items-center gap-2">
              <InternalChip />
              <b className="text-text">{tm ? SWAP.timeAndMaterials : SWAP.fixedPrice}</b>
              <span className="flex-1" />
              {money2.billing === 'time_materials'
                ? <b className="text-accent-dark">{SWAP.billsAtNewPrice}</b>
                : <b className={money2.visitPays.tone === 'under' ? 'text-accent-dark' : 'text-warn'}>
                    {SWAP.visitPays(signedMoney(money2.visitPays))}
                  </b>}
            </div>
            <div className="mt-1.5">
              {money2.billing === 'time_materials'
                ? fill(
                  SWAP.tmNote(count(money2.qty), money(money2.unitPrice), SLOT, money(money2.billedWas),
                    signedMoney(money2.costDelta), signedMoney(money2.priceDelta)),
                  <b className="text-text">{money(money2.billedNow)}</b>,
                )
                : SWAP.fixedNote(
                  options.customerFirstName ?? '', count(money2.qty), money(money2.unitNow), money(money2.now),
                  money(money2.planned),
                )}
            </div>
          </div>
        )}

        {fixedLike && options.salesperson && (
          <TickRow
            on={askChangeOrder}
            onToggle={() => setAskChangeOrder((was) => !was)}
            title={SWAP.askChangeOrder(options.salesperson.name)}
            sub={SWAP.askChangeOrderSub(options.salesperson.firstName, options.customerFirstName ?? '')}
          />
        )}
        {tm && (
          <TickRow
            on={tellCustomer}
            onToggle={() => setTellCustomer((was) => !was)}
            title={SWAP.tellTheCustomer}
            sub={SWAP.tellTheCustomerSub}
          />
        )}

        <button
          type="button"
          role="checkbox"
          aria-checked={tellCrew}
          onClick={() => setTellCrew((was) => !was)}
          className="flex items-center gap-2.5 text-left"
        >
          <TickBox on={tellCrew} />
          <span className="text-[12.5px]"><b>{SWAP.tellTheCrew}</b> {SWAP.tellTheCrewSub}</span>
        </button>

        {error && <p className="m-0 text-[12.5px] text-danger">{error}</p>}
      </div>
    </VisitPopup>
  )
}

/** The line as it is — Board 4's grey box (the material glyph, the name, its line, its chip). */
function LineBox({ name, sub, chip }: { name: string; sub: string; chip: React.ReactNode }) {
  return (
    <div className="flex items-center gap-2.5 rounded-[10px] border border-line-faint bg-surface-alt px-3 py-2.5">
      <span className="inline-flex text-text-muted"><VisitGlyph name="material" size={16} stroke={2} /></span>
      <div className="flex min-w-0 flex-col">
        <b className="text-[12.5px]">{name}</b>
        <span className="text-[11.5px] text-text-muted">{sub}</span>
      </div>
      <span className="flex-1" />
      {chip}
    </div>
  )
}

/** A choice — Board 4's radio card: the item and its tag, why it fits, where it is, and (internal) its price. */
function ChoiceCard({ choice, tag, on, onPick }: {
  choice: SwapChoice
  tag: string | null
  on: boolean
  onPick: () => void
}) {
  const fit = choice.fit.colour
    ? choice.fit.colour.kind === 'matches' && choice.fit.sameSize
      ? SWAP.matches(choice.fit.colour.what)
      : choice.fit.colour.kind === 'lighter' ? SWAP.lighterThan(choice.fit.colour.what) : null
    : null
  const stock = choice.stock
    ? choice.stock.where === 'vendor'
      ? SWAP.vendorHas(choice.stock.vendor.name, count(choice.stock.qty))
      : SWAP.atYardNow(count(choice.stock.qty))
    : null
  return (
    <button
      type="button"
      role="radio"
      aria-checked={on}
      onClick={onPick}
      className={cn(
        'flex items-start gap-2.5 rounded-[11px] border-[1.5px] px-3 py-[11px] text-left',
        on ? 'border-accent bg-green-band-bg shadow-[0_0_0_3px_rgb(var(--color-accent-ring))]' : 'border-line bg-surface hover:bg-hover',
      )}
    >
      <RadioDot on={on} size={17} />
      <div className="flex min-w-0 flex-1 flex-col">
        <div className="flex min-w-0 items-center gap-1.5">
          <b className="text-[12.5px]">{choice.item.name}</b>
          {tag && (
            <span className="inline-flex h-[19px] shrink-0 items-center whitespace-nowrap rounded-[4px] bg-accent-soft px-1.5
                             text-[11px] font-semibold text-accent-dark">
              {tag}
            </span>
          )}
        </div>
        {fit && <span className="mt-0.5 text-[11.5px] text-text-muted">{fit}</span>}
        {stock && <span className="mt-[3px] text-[11px] font-semibold text-accent-dark">{stock}</span>}
      </div>
      {choice.internal && (
        <div className="flex flex-col">
          <b className="whitespace-nowrap text-right text-[12.5px]">{money(choice.internal.unitCost)}</b>
          <span
            className={cn(
              'whitespace-nowrap text-right text-[11px] font-bold',
              choice.internal.unitDelta.tone === 'under' ? 'text-accent-dark' : choice.internal.unitDelta.tone === 'zero' ? 'text-text-faint' : 'text-warn',
            )}
          >
            {SWAP.each(signedMoney(choice.internal.unitDelta))}
          </span>
        </div>
      )}
    </button>
  )
}

/** The bordered tick rows — *Ask Tom Reyes for a change order* (A), *Tell the customer* (B). */
function TickRow({ on, onToggle, title, sub }: { on: boolean; onToggle: () => void; title: string; sub: string }) {
  return (
    <button
      type="button"
      role="checkbox"
      aria-checked={on}
      onClick={onToggle}
      className="flex items-start gap-2.5 rounded-[10px] border border-line px-3 py-2.5 text-left"
    >
      <TickBox on={on} />
      <div className="flex flex-col">
        <b className="text-[12.5px]">{title}</b>
        <span className="text-[11.5px] leading-[1.4] text-text-muted">{sub}</span>
      </div>
    </button>
  )
}

/**
 * *＋ Find another item · or split it (some now, the rest later)* — a search
 * of the catalog, and the split's quantity and source. Not drawn open on a
 * board: built from the board's own words (DEVIATION REQUEST).
 */
function FindAnother({ read, line, split, onSplit, onPicked }: {
  read: VisitRead
  line: MaterialLine
  split: { qty: string; source: MaterialSource } | null
  onSplit: (split: { qty: string; source: MaterialSource } | null) => void
  onPicked: (choice: SwapChoice) => void
}) {
  const [q, setQ] = useState('')
  const [found, setFound] = useState<MaterialCatalog['items']>([])
  const latest = useRef(0)

  useEffect(() => {
    const ticket = ++latest.current
    if (!q.trim()) { setFound([]); return }
    const timer = window.setTimeout(() => {
      request<MaterialCatalog>(`${visitPath(read.id, '/materials/catalog')}?q=${encodeURIComponent(q.trim())}`)
        .then((body) => { if (ticket === latest.current) setFound(body.items) })
        .catch(() => { /* nothing found is what the list then shows */ })
    }, 150)
    return () => window.clearTimeout(timer)
  }, [q, read.id])

  async function pick(itemId: string) {
    const body = await request<SwapOptions>(
      `${visitPath(read.id, `/materials/${encodeURIComponent(line.id)}/swap-options`)}?itemId=${encodeURIComponent(itemId)}`,
    )
    if (body.kind === 'choices' && body.picked) onPicked(body.picked)
  }

  return (
    <div className="flex flex-col gap-2 rounded-[10px] border border-line-faint bg-surface-alt p-2.5">
      <div className={FIELD_BOX}>
        <span className="mr-2 inline-flex text-text-faint"><VisitGlyph name="search" size={15} stroke={2} /></span>
        <input
          value={q}
          onChange={(event) => setQ(event.target.value)}
          placeholder={MATERIALS.find}
          aria-label={MATERIALS.find}
          className={FIELD_INPUT}
        />
      </div>
      {found.length > 0 && (
        <div className="overflow-hidden rounded-[10px] border border-line bg-surface">
          {found.map(({ item, unit }) => (
            <button
              key={item.id}
              type="button"
              onClick={() => void pick(item.id)}
              className="flex w-full items-center gap-2 border-t border-line-faint px-3 py-2 text-left first:border-t-0 hover:bg-hover"
            >
              <b className="min-w-0 flex-1 truncate text-[12.5px]">{item.name}</b>
              {unit && <span className="text-[11.5px] text-text-muted">{unit.one}</span>}
            </button>
          ))}
        </div>
      )}
      <div className="grid grid-cols-[1fr_auto] items-end gap-3">
        <div className="min-w-0">
          <FieldLabel>{ORDER.quantity}</FieldLabel>
          <div className={FIELD_BOX}>
            <input
              inputMode="decimal"
              value={split?.qty ?? ''}
              onChange={(event) => onSplit(event.target.value ? { qty: event.target.value, source: split?.source ?? line.source } : null)}
              aria-label={ORDER.quantity}
              className={FIELD_INPUT}
            />
            {line.unit && <span className="ml-1.5 text-[12px] text-text-faint">{line.unit.many}</span>}
          </div>
        </div>
      </div>
      {split && (
        <div role="radiogroup" aria-label={MATERIALS.head.comesFrom} className="flex flex-wrap gap-1.5">
          {MATERIAL_SOURCES.map((source) => (
            <ChoiceChip key={source} size={28} on={split.source === source} onClick={() => onSplit({ ...split, source })}>
              {MATERIALS.source[source]}
            </ChoiceChip>
          ))}
        </div>
      )}
    </div>
  )
}

// ---------------------------------------------------------------------------
// C — on a placed order, delivered, loaded or used
// ---------------------------------------------------------------------------

function Blocked({ read, line, options, phone, onClose }: {
  read: VisitRead
  line: MaterialLine
  options: Extract<SwapOptions, { kind: 'blocked' }>
  phone: boolean
  onClose: () => void
}) {
  const navigate = useNavigate()
  const write = useVisitWrite(read.id)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const tz = read.timeZone
  const { line: swapLine, po, buyer } = options
  const name = swapLine.names.detail ? `${swapLine.names.name}, ${swapLine.names.detail}` : swapLine.names.name
  const day = swapLine.day !== null ? String(swapLine.day) : ''

  async function askToCancel() {
    setBusy(true)
    setError(null)
    try {
      await write('POST', `/materials/${encodeURIComponent(line.id)}/ask-to-cancel`, { note: '' }, { tab: 'materials' })
      onClose()
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : String(cause))
    } finally {
      setBusy(false)
    }
  }

  return (
    <VisitPopup
      width={402}
      phone={phone}
      title={SWAP.title(swapLine.names.shortName)}
      sub={SWAP.subOrdered}
      mark={<VisitTile glyph="swap" tone="grey" size={34} />}
      onClose={onClose}
      asDrawn="vp-04 C"
      footer={<VisitButton size={phone ? 'phone' : 'popup'} onClick={onClose}>{SWAP.blocked.close}</VisitButton>}
    >
      <div className="flex flex-col gap-3">
        <LineBox name={name} sub={SWAP.qtyDay(qtyText(swapLine.qty, swapLine.unit), day)} chip={<MaterialChip state={swapLine.state} />} />

        {options.why === 'po_placed' && po && (
          <div className="flex items-start gap-[9px] rounded-[10px] border border-amber-band-line bg-amber-band-bg px-3 py-2.5
                          text-[12.5px] leading-[1.45] text-amber-band-text">
            <VisitGlyph name="warn" size={15} stroke={2} />
            <div>
              <b>{SWAP.blocked.title}</b>{' '}
              {SWAP.blocked.line(po.po.number, po.vendor.name, timeIn(tz, po.placedAt, 'day'), timeIn(tz, po.placedAt, 'ampm'))}
            </div>
          </div>
        )}

        {(po || buyer) && (
          <div className="overflow-hidden rounded-[10px] border border-line">
            {po && (
              <ActionRow
                tile="info"
                glyph="cart"
                title={SWAP.blocked.open(po.po.number)}
                sub={SWAP.blocked.openSub(
                  RAIL.purchaseOrders.status[po.po.status as keyof typeof RAIL.purchaseOrders.status] ?? po.po.status,
                  po.vendor.name,
                  po.arrives ? (po.arrives.relative ? COMMON[po.arrives.relative] : timeIn(tz, po.arrives.at, 'day')) : '',
                )}
                onClick={() => { onClose(); navigate(poHref(po.po)) }}
              />
            )}
            {buyer && (
              <ActionRow
                tile="teal"
                glyph="people"
                title={SWAP.blocked.askCancel(buyer.name)}
                sub={SWAP.blocked.askCancelSub}
                disabled={busy}
                onClick={() => void askToCancel()}
              />
            )}
          </div>
        )}

        <p className="m-0 text-[12px] leading-[1.5] text-text-muted">{SWAP.blocked.bought}</p>
        {error && <p className="m-0 text-[12.5px] text-danger">{error}</p>}
      </div>
    </VisitPopup>
  )
}

/** A row in Board 4 C's box — a 30 px tile, the words, a chevron. */
function ActionRow({ tile, glyph, title, sub, onClick, disabled = false }: {
  tile: 'info' | 'teal'
  glyph: 'cart' | 'people'
  title: string
  sub: string
  onClick: () => void
  disabled?: boolean
}) {
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={onClick}
      className="flex min-h-12 w-full items-center gap-[11px] border-t border-line-faint px-[18px] py-[7px] text-left
                 first:border-t-0 hover:bg-hover disabled:opacity-60"
    >
      <span
        className={cn(
          'inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-button border',
          tile === 'info' ? 'border-info-line bg-info-bg text-info' : 'border-teal-band-line bg-teal-band-bg text-teal-band-text',
        )}
      >
        <VisitGlyph name={glyph} size={15} stroke={1.9} />
      </span>
      <span className="flex min-w-0 flex-1 flex-col">
        <b className="whitespace-nowrap text-[13px] font-semibold">{title}</b>
        <span className="mt-px text-[11.5px] text-text-muted">{sub}</span>
      </span>
      <span className="inline-flex text-text-faint"><VisitGlyph name="chevronRight" size={14} stroke={2} /></span>
    </button>
  )
}
